import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../api/axios";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (username, password) => {
    try {
      const res = await api.post("/login", { username, password });

      // Store both tokens
      localStorage.setItem("access_token", res.data.access_token);
      localStorage.setItem("refresh_token", res.data.refresh_token);

      // Fetch user profile
      const userRes = await api.get("/profile");
      setUser(userRes.data);

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Login failed",
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate refresh token on server
      await api.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear tokens from localStorage
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(null);
      
      // Redirect to appropriate login page based on current route
      const currentPath = window.location.pathname;
      if (currentPath.startsWith("/admin")) {
        window.location.href = "/admin/login";
      } else {
        window.location.href = "/login";
      }
    }
  };

  const refreshToken = async () => {
    try {
      const refresh_token = localStorage.getItem("refresh_token");
      if (!refresh_token) {
        throw new Error("No refresh token available");
      }

      const response = await api.post("/refresh", { refresh_token });
      const newAccessToken = response.data.access_token;

      localStorage.setItem("access_token", newAccessToken);
      return newAccessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // If refresh fails, logout user
      logout();
      throw error;
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/profile?t=${new Date().getTime()}`);
      setUser(res.data);
    } catch (error) {
      console.error("Fetch user error:", error);
      // If fetching user fails, try to refresh token
      if (error.response?.status === 401) {
        try {
          await refreshToken();
          const res = await api.get("/profile");
          setUser(res.data);
        } catch (refreshError) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if user is authenticated on app start
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem("access_token");
      const refreshTokenStored = localStorage.getItem("refresh_token");

      if (accessToken && refreshTokenStored) {
        await fetchUser();
      } else {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        fetchUser,
        refreshToken,
        setUser,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
